/**
 * Job Site Safety Inspection — schema interpreter.
 *
 * The questions themselves no longer live here. They are authored in the admin, published as
 * numbered revisions and synced to the device; this module is the code that renders, scores and
 * validates whatever revision it is handed. Keeping the logic separate from the data is what lets an
 * old draft be reopened against the exact revision it was started on.
 *
 * A published revision looks like:
 *   { version, hash, publishedAt, sections: [{ key, title, questions: [question] }] }
 *
 * and every question arrives fully resolved — no inherited defaults left to work out on the client:
 *   { key, number, label, type, required, score, hasComment, note, dependsOn, options }
 *
 * `options` is the single source of truth for answering behaviour:
 *   { value, score, flag, severity, requireComment }
 *   score: number -> counts toward earned AND possible; null -> outside the percentage entirely
 *   flag: the answer lands in the flagged-items list
 *   severity: good | warn | bad | neutral — drives colour in the app and in the PDF
 *   requireComment: the answer must be explained before the report can be submitted
 *
 * Answer storage model (form.answers) is unchanged and is a hard contract — stored photos on the
 * server are keyed by it:
 *   answers[q.key]              -> value
 *   answers[q.key + '_comment'] -> correction / explanation text
 *   answers[q.key + '_photo']   -> photo (base64 data-url on the client; stored path after submit)
 *                                  — only in older drafts and reports: "Free text + photo" now uses
 *                                  the list below like every other question
 *   answers[q.key + '_photos']  -> photo list [{ path, caption }], for any question the admin allowed
 *                                  photos on (question.photos = { max }); path follows the same
 *                                  data-url / stored path rule. Older drafts hold plain strings —
 *                                  read as photos without a caption.
 */

export const ANSWER = { YES: 'Yes', NO: 'No', NA: 'N/A' };

export const SEVERITY = { GOOD: 'good', WARN: 'warn', BAD: 'bad', NEUTRAL: 'neutral' };

export const DIVISION_OPTIONS = [
    { code: '50', label: '50 - Electrical' },
    { code: '35', label: '35 - Mechanical' },
    { code: '58', label: '58 - Piping' },
    { code: '99', label: '99 - Charleston' },
    { code: '23', label: '23 - Columbia' },
    { code: '68', label: '68 - TN' },
];

/**
 * Header / Title Page fields. These stay in code on purpose: each one is a bespoke widget (employee
 * picker, job-site picker, geolocation lookup), so they are not something an admin can meaningfully
 * author as a "question".
 */
export const HEADER_FIELDS = {
    conductedOn: { label: 'Conducted on', type: 'datetime', required: true, autofill: 'now' },
    divisions: { label: 'Division (If Known)', type: 'multi_choice', required: false, autofill: 'job', options: DIVISION_OPTIONS },
    siteSupervisor: { label: 'Site Supervisor', type: 'employee_text', required: true },
    jobSite: { label: 'Location', type: 'location', required: true },
    // Адрес спрашиваем только у локации, вписанной руками: у джоба из списка он
    // уже есть в справочнике и уходит в отчёт сам.
    exactLocation: { label: 'Exact Location (Address)', type: 'text', required: true, onlyForManualLocation: true, autofill: 'job' },
    personnelCount: { label: 'Number of Personnel Onsite (Include Subcontractors)', type: 'number', required: true, autofill: 'manual' },
    workDescription: { label: 'Brief Description of Work being performed', type: 'text', required: true, autofill: 'job' },
};

// DOM ids used by the form to highlight & scroll to an unanswered required item.
export const headerDomId = (field) => `jssi-hf-${field}`;
export const questionDomId = (key) => `jssi-q-${key}`;
export const SIGNATURE_DOM_ID = 'jssi-signature';
export const SUPERVISOR_SIGNATURE_DOM_ID = 'jssi-supervisor-signature';

/** Question types the app knows how to draw. Anything else is rendered read-only, never dropped. */
export const QUESTION_TYPES = ['ynna', 'gate', 'rating', 'text', 'textPhoto'];

const TEXTUAL_TYPES = ['text', 'textPhoto'];

const isEmptyValue = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * How many photos a question takes: 0 when the admin has not allowed any.
 * "Free text + photo" has always taken one photo — the Photos setting in the editor only raises that.
 */
export const photoLimit = (q) => {
    const max = Number(q?.photos?.max);
    if (Number.isFinite(max) && max > 0) return Math.floor(max);
    return q?.type === 'textPhoto' ? 1 : 0;
};

/** The answers key a question's photo list is stored under. */
export const photosKey = (key) => `${key}_photos`;

/** Longest caption kept — the server cuts at the same length. */
export const PHOTO_CAPTION_MAX = 500;

/** One photo of the list as { path, caption }: a plain string is a photo from before captions. */
export const photoEntry = (raw) => {
    if (typeof raw === 'string') return { path: raw, caption: '' };
    return {
        path: typeof raw?.path === 'string' ? raw.path : '',
        caption: typeof raw?.caption === 'string' ? raw.caption : '',
    };
};

/** A question's photo list, whatever shape the draft holds it in. */
export const photosOf = (answers, key) => {
    const list = answers?.[photosKey(key)];
    return Array.isArray(list) ? list.map(photoEntry).filter((p) => p.path !== '') : [];
};

/**
 * Wrap a published schema revision in the helpers the form needs.
 *
 * Derived lookups are built once here rather than on every render, which matters: the form
 * recomputes score and flags on each keystroke over ~130 questions.
 */
export function makeSchema(published) {
    const sections = Array.isArray(published?.sections) ? published.sections : [];

    const allQuestions = sections.flatMap((section) =>
        (section.questions || []).map((q) => ({ ...q, sectionKey: section.key, sectionTitle: section.title }))
    );
    const questionByKey = Object.fromEntries(allQuestions.map((q) => [q.key, q]));

    /** The option row matching an answer, or null when unanswered / no longer offered. */
    const optionFor = (q, value) => (q.options || []).find((o) => o.value === value) || null;

    /**
     * A question is visible unless its dependency parent is hidden or answered differently.
     *
     * The visited set is a safety net rather than a nicety: a schema whose dependencies form a loop
     * would otherwise recurse forever and freeze the form on the iPad, offline, mid-inspection. The
     * admin cannot publish such a schema, but a device may still hold an older cached one.
     */
    function isVisible(q, answers, visited) {
        if (!q || !q.dependsOn) return true;

        const seen = visited || new Set();
        if (seen.has(q.key)) return false;
        seen.add(q.key);

        const parent = questionByKey[q.dependsOn.key];
        if (parent && !isVisible(parent, answers, seen)) return false;

        return answers[q.dependsOn.key] === q.dependsOn.value;
    }

    /** Effective per-question settings. Already resolved server-side; defaults guard old caches. */
    function qSettings(q) {
        return {
            required: q.required !== undefined ? q.required : true,
            score: q.score !== undefined ? q.score : false,
            hasComment: q.hasComment !== undefined ? q.hasComment : false,
        };
    }

    /** Full label including the printed hierarchical number (e.g. "1.8.1. Was this issue fixed?"). */
    function qLabel(q) {
        return q.number ? `${q.number}. ${q.label}` : q.label;
    }

    /**
     * Weighted score over visible questions. An answer counts only when its option carries points;
     * "N/A" and unanswered questions stay out of the denominator so they cannot drag the result down.
     */
    function computeScore(answers) {
        let earned = 0;
        let possible = 0;

        allQuestions.forEach((q) => {
            if (!qSettings(q).score || !isVisible(q, answers)) return;

            const option = optionFor(q, answers[q.key]);
            if (option && option.score !== null && option.score !== undefined) {
                earned += option.score;
                possible += 1;
            }
        });

        const percent = possible > 0 ? Math.round((earned / possible) * 100) : null;
        return { earned, possible, percent };
    }

    /** Flagged items = visible answers whose chosen option is marked as a finding. */
    function computeFlags(answers) {
        const flags = [];

        allQuestions.forEach((q) => {
            if (!isVisible(q, answers)) return;

            const value = answers[q.key];
            const option = optionFor(q, value);
            if (!option || !option.flag) return;

            flags.push({
                sectionTitle: q.sectionTitle,
                number: q.number || null,
                label: q.label,
                value,
                severity: option.severity || SEVERITY.BAD,
                comment: answers[`${q.key}_comment`] || '',
            });
        });

        return flags;
    }

    /**
     * Required header fields + visible required questions that are unanswered, plus answers that owe
     * an explanation. Returns [{ id, label }] — id is the DOM id to scroll to, label is for the popup.
     *
     * context.manualLocation — локация вписана руками, а не выбрана из джобов: только тогда
     * обязателен Exact Location (у джоба из списка поля в форме нет вовсе).
     */
    function getMissingRequired(form, context = {}) {
        const missing = [];
        const answers = form.answers || {};

        Object.entries(HEADER_FIELDS).forEach(([key, field]) => {
            if (!field.required) return;
            if (field.onlyForManualLocation && !context.manualLocation) return;
            if (isEmptyValue(typeof form[key] === 'string' ? form[key].trim() : form[key])) {
                missing.push({ id: headerDomId(key), label: field.label });
            }
        });

        allQuestions.forEach((q) => {
            if (!isVisible(q, answers)) return;

            const settings = qSettings(q);
            const value = answers[q.key];

            if (settings.required && isEmptyValue(value)) {
                missing.push({ id: questionDomId(q.key), label: `${q.sectionTitle}: ${qLabel(q)}` });
                return;
            }

            const option = optionFor(q, value);
            if (option && option.requireComment && settings.hasComment && isEmptyValue(answers[`${q.key}_comment`])) {
                missing.push({
                    id: questionDomId(q.key),
                    label: `${q.sectionTitle}: ${qLabel(q)} — comment required for "${value}"`,
                });
            }
        });

        if (!form.signature) missing.push({ id: SIGNATURE_DOM_ID, label: 'Inspector Signature' });
        if (!form.supervisorSignature) {
            missing.push({ id: SUPERVISOR_SIGNATURE_DOM_ID, label: 'Site Supervisor Signature' });
        }

        return missing;
    }

    /**
     * Build the render-ready snapshot the PDF and the admin report are drawn from.
     *
     * This is what makes a report readable forever: it describes itself, so a revision that has since
     * been edited — or deleted outright — never changes how an already-submitted report looks.
     * Only visible questions are included; photos pass through as-is (data-url client-side).
     */
    function buildReportData(form) {
        const answers = form.answers || {};

        const reportSections = sections
            .map((section) => {
                const questions = (section.questions || [])
                    .filter((q) => isVisible(questionByKey[q.key] || q, answers))
                    .map((q) => {
                        const settings = qSettings(q);
                        const value = answers[q.key] ?? '';
                        const option = optionFor(q, value);
                        const photos = photosOf(answers, q.key);

                        return {
                            key: q.key,
                            number: q.number || null,
                            label: q.label,
                            type: q.type,
                            value,
                            comment: settings.hasComment ? (answers[`${q.key}_comment`] || '') : '',
                            photo: q.type === 'textPhoto' ? (answers[`${q.key}_photo`] || '') : '',
                            // Only when there are any: 130 empty lists would just be noise in every
                            // stored report. The PDF and the admin page print them under the question.
                            ...(photos.length ? { photos } : {}),
                            scored: settings.score,
                            flagged: !!(option && option.flag),
                            severity: option ? (option.severity || SEVERITY.NEUTRAL) : SEVERITY.NEUTRAL,
                            note: q.note || '',
                        };
                    });

                return { key: section.key, title: section.title, questions };
            })
            .filter((section) => section.questions.length > 0);

        return {
            sections: reportSections,
            flagged: computeFlags(answers),
            score: computeScore(answers),
            schemaVersion: published?.version ?? null,
            schemaHash: published?.hash ?? null,
        };
    }

    return {
        version: published?.version ?? null,
        hash: published?.hash ?? null,
        sections,
        allQuestions,
        questionByKey,
        optionFor,
        isVisible,
        qSettings,
        qLabel,
        computeScore,
        computeFlags,
        getMissingRequired,
        buildReportData,
    };
}

/** True for the free-text types, which have no answer options. */
export const isTextualType = (type) => TEXTUAL_TYPES.includes(type);
